import {
  getReview as publicGetReview,
  createReview as publicCreateReview,
  bulkCreateReview as publicBulkCreateReview,
  updateReview as publicUpdateReview,
  deleteReview as publicDeleteReview,
  bulkDeleteReviews as publicBulkDeleteReviews,
  queryReviews as publicQueryReviews,
  typedQueryReviews as publicTypedQueryReviews,
  setReply as publicSetReply,
  removeReply as publicRemoveReply,
  updateModerationStatus as publicUpdateModerationStatus,
  bulkUpdateModerationStatus as publicBulkUpdateModerationStatus,
  countReviews as publicCountReviews,
} from './reviews-v1-review-reviews.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  QueryReviewsOptions,
  ReviewQuery,
  ReviewsQueryBuilder,
  typedQueryReviews as universalTypedQueryReviews,
} from './reviews-v1-review-reviews.universal.js';
import { onReviewCreated as publicOnReviewCreated } from './reviews-v1-review-reviews.public.js';
import { onReviewDeleted as publicOnReviewDeleted } from './reviews-v1-review-reviews.public.js';
import { onReviewModerationStatusChanged as publicOnReviewModerationStatusChanged } from './reviews-v1-review-reviews.public.js';
import { onReviewPublished as publicOnReviewPublished } from './reviews-v1-review-reviews.public.js';
import { onReviewUpdated as publicOnReviewUpdated } from './reviews-v1-review-reviews.public.js';

function customQueryReviews(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryReviewsOptions) =>
      publicQueryReviews(httpClient)(options),
    typedQueryFunction: (query: ReviewQuery, options?: QueryReviewsOptions) =>
      publicTypedQueryReviews(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: ReviewQuery,
    options?: QueryReviewsOptions
  ): ReturnType<typeof universalTypedQueryReviews>;
  function overloadedQuery(options?: QueryReviewsOptions): ReviewsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: ReviewQuery | QueryReviewsOptions,
    options?: QueryReviewsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const getReview: MaybeContext<
  BuildRESTFunction<typeof publicGetReview> & typeof publicGetReview
> = /*#__PURE__*/ createRESTModule(publicGetReview);
export const createReview: MaybeContext<
  BuildRESTFunction<typeof publicCreateReview> & typeof publicCreateReview
> = /*#__PURE__*/ createRESTModule(publicCreateReview);
export const bulkCreateReview: MaybeContext<
  BuildRESTFunction<typeof publicBulkCreateReview> &
    typeof publicBulkCreateReview
> = /*#__PURE__*/ createRESTModule(publicBulkCreateReview);
export const updateReview: MaybeContext<
  BuildRESTFunction<typeof publicUpdateReview> & typeof publicUpdateReview
> = /*#__PURE__*/ createRESTModule(publicUpdateReview);
export const deleteReview: MaybeContext<
  BuildRESTFunction<typeof publicDeleteReview> & typeof publicDeleteReview
> = /*#__PURE__*/ createRESTModule(publicDeleteReview);
export const bulkDeleteReviews: MaybeContext<
  BuildRESTFunction<typeof publicBulkDeleteReviews> &
    typeof publicBulkDeleteReviews
> = /*#__PURE__*/ createRESTModule(publicBulkDeleteReviews);
export const setReply: MaybeContext<
  BuildRESTFunction<typeof publicSetReply> & typeof publicSetReply
> = /*#__PURE__*/ createRESTModule(publicSetReply);
export const removeReply: MaybeContext<
  BuildRESTFunction<typeof publicRemoveReply> & typeof publicRemoveReply
> = /*#__PURE__*/ createRESTModule(publicRemoveReply);
export const updateModerationStatus: MaybeContext<
  BuildRESTFunction<typeof publicUpdateModerationStatus> &
    typeof publicUpdateModerationStatus
> = /*#__PURE__*/ createRESTModule(publicUpdateModerationStatus);
export const bulkUpdateModerationStatus: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateModerationStatus> &
    typeof publicBulkUpdateModerationStatus
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateModerationStatus);
export const countReviews: MaybeContext<
  BuildRESTFunction<typeof publicCountReviews> & typeof publicCountReviews
> = /*#__PURE__*/ createRESTModule(publicCountReviews);
export const queryReviews: MaybeContext<
  BuildRESTFunction<typeof customQueryReviews> & typeof customQueryReviews
> = /*#__PURE__*/ createRESTModule(customQueryReviews);
/**
 * Triggered when a review is created.
 */
export const onReviewCreated: BuildEventDefinition<
  typeof publicOnReviewCreated
> &
  typeof publicOnReviewCreated = createEventModule(publicOnReviewCreated);
/**
 * Triggered when a review is deleted.
 */
export const onReviewDeleted: BuildEventDefinition<
  typeof publicOnReviewDeleted
> &
  typeof publicOnReviewDeleted = createEventModule(publicOnReviewDeleted);
/**
 * Triggered when a review's moderation status is changed or a review is created.
 */
export const onReviewModerationStatusChanged: BuildEventDefinition<
  typeof publicOnReviewModerationStatusChanged
> &
  typeof publicOnReviewModerationStatusChanged = createEventModule(
  publicOnReviewModerationStatusChanged
);
/**
 * Triggered when a review's moderation status is set to `"APPROVED"`.
 */
export const onReviewPublished: BuildEventDefinition<
  typeof publicOnReviewPublished
> &
  typeof publicOnReviewPublished = createEventModule(publicOnReviewPublished);
/**
 * Triggered when a review is updated.
 */
export const onReviewUpdated: BuildEventDefinition<
  typeof publicOnReviewUpdated
> &
  typeof publicOnReviewUpdated = createEventModule(publicOnReviewUpdated);

export {
  AuthorType,
  ModerationModerationStatus,
  OriginType,
  SourceType,
  WebhookIdentityType,
  SortOrder,
  UpdateModerationStatusRequestModerationStatus,
  ModerationStatus,
} from './reviews-v1-review-reviews.universal.js';
export {
  Review,
  ReviewContent,
  Media,
  MediaMediaOneOf,
  VideoResolution,
  Author,
  Reply,
  Moderation,
  Origin,
  Source,
  SourceSourceTypeOptionsOneOf,
  ReviewRequestOptions,
  ReviewPublished,
  ModerationStatusChanged,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  Empty,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  GetReviewRequest,
  GetReviewResponse,
  CreateReviewAndContactRequest,
  CreateReviewResponse,
  CreateReviewRequest,
  BulkCreateReviewRequest,
  BulkCreateReviewResponse,
  BulkReviewResult,
  ItemMetadata,
  ApplicationError,
  BulkActionMetadata,
  UpdateReviewContentRequest,
  UpdateReviewContentResponse,
  UpdateReviewRequest,
  UpdateReviewResponse,
  DeleteReviewRequest,
  DeleteReviewResponse,
  BulkDeleteReviewsRequest,
  BulkDeleteReviewsResponse,
  QueryReviewsRequest,
  QueryV2,
  Sorting,
  CursorPaging,
  QueryReviewsResponse,
  PagingMetadataV2,
  Cursors,
  SetReplyRequest,
  SetReplyResponse,
  RemoveReplyRequest,
  RemoveReplyResponse,
  UpdateModerationStatusRequest,
  UpdateModerationStatusResponse,
  BulkUpdateModerationStatusRequest,
  BulkUpdateModerationStatusResponse,
  CountReviewsRequest,
  CountReviewsResponse,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  ReviewCreatedEnvelope,
  ReviewDeletedEnvelope,
  ReviewModerationStatusChangedEnvelope,
  ReviewPublishedEnvelope,
  ReviewUpdatedEnvelope,
  GetReviewOptions,
  BulkCreateReviewOptions,
  UpdateReview,
  BulkDeleteReviewsOptions,
  QueryReviewsOptions,
  ReviewsQueryResult,
  ReviewsQueryBuilder,
  ReviewQuerySpec,
  UpdateModerationStatusOptions,
  BulkUpdateModerationStatusOptions,
  CountReviewsOptions,
} from './reviews-v1-review-reviews.universal.js';
export { utils } from './reviews-v1-review-reviews.universal.js';
export {
  AuthorTypeWithLiterals,
  ModerationModerationStatusWithLiterals,
  OriginTypeWithLiterals,
  SourceTypeWithLiterals,
  WebhookIdentityTypeWithLiterals,
  SortOrderWithLiterals,
  UpdateModerationStatusRequestModerationStatusWithLiterals,
  ModerationStatusWithLiterals,
  CountReviewsApplicationErrors,
  CommonQueryWithEntityContext,
  ReviewQuery,
} from './reviews-v1-review-reviews.universal.js';
